import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const workSteps = [
  {
    icon: 'work-icon-01.svg',
    title: 'Step 1 - Process Discovery and Automation Blueprint',
    desc: 'We map the workflow end to end, identify bottlenecks and failure points, define success metrics, and design a practical automation plan with clear ownership.',
  },
  {
    icon: 'work-icon-02.svg',
    title: 'Step 2 - Integration Architecture and Data Contracts',
    desc: 'We define systems of record, API contracts, data mapping, permissions, and validation rules, including how retries, idempotency, and exceptions should behave.',
  },
  {
    icon: 'work-icon-03.svg',
    title: 'Step 3 - Build, Test, and Hardening',
    desc: 'We implement automations and integrations with test coverage, sandbox validation, and realistic data samples, including negative tests for edge cases and failures.',
  },
  {
    icon: 'work-icon-04.svg',
    title: 'Step 4 - Rollout with Monitoring and Safe Controls',
    desc: 'We deploy using staged rollouts, feature flags where needed, and operational guardrails like rate limits, alerting, and runbooks for support teams.',
  },
  {
    icon: 'work-icon-05.svg',
    title: 'Step 5 - Optimization, Governance, and Continuous Improvement',
    desc: 'We measure ROI and reliability, reduce exception rates, improve throughput, and establish governance so workflows stay accurate as tools and policies change.',
  },
];

const faqItems = [
  {
    question: 'What types of workflows are best suited for automation?',
    answer:
      'Workflows with repeatable steps, clear inputs, and measurable outcomes are ideal. Common examples include approvals and routing, ticket triage, lead qualification handoffs, invoice and purchase order flows, onboarding and access provisioning, reporting triggers, and data synchronization between systems.',
  },
  {
    question: 'Do you automate using no-code tools or custom development?',
    answer:
      'Both. We select the approach based on reliability needs, complexity, and security requirements. No-code and low-code platforms can be excellent for standard integrations and routing. Custom automation is typically better when workflows need strict validation, complex logic, high throughput, or strong audit and compliance controls.',
  },
  {
    question: 'How do you prevent automations from breaking when systems change?',
    answer:
      'We design automation with durable contracts and monitoring. This includes schema validation, idempotency keys for write actions, safe retries, rate limiting, and alerting on anomalies. We also document ownership and change processes so updates to APIs, fields, or policies do not silently break operations.',
  },
  {
    question: 'How do you handle errors, exceptions, and edge cases?',
    answer:
      'Every automated workflow includes a defined exception path. We route uncertain or failed cases to a review queue, capture error context for rapid diagnosis, and implement fallback behavior when external systems are unavailable. For high-impact actions, we add approvals or human review gates.',
  },
  {
    question: 'Can you integrate legacy systems or tools without modern APIs?',
    answer:
      'Yes. When modern APIs are not available, we use practical alternatives such as database connectors, file-based exchange, email parsing, middleware, or controlled RPA where appropriate. We prefer API and event-based patterns where possible because they are more reliable and auditable long term.',
  },
  {
    question: 'How do you measure automation ROI in a credible way?',
    answer:
      'We measure more than time saved. Typical ROI metrics include cycle time reduction, throughput increase, rework and error rate reduction, SLA improvements, and risk reduction through better auditability. We baseline metrics before automation and report impact after rollout with conservative assumptions.',
  },
  {
    question: 'Can automation include AI components?',
    answer:
      'Yes, when it improves interpretation of unstructured inputs like emails, documents, or free text. In those cases, AI is used to classify or extract, and deterministic automation validates and executes. We use confidence thresholds and review queues to keep outcomes safe and measurable.',
  },
];

const ServiceWorkflowAutomation = () => {
  useEffect(() => {
    if (window.WOW) new window.WOW().init();
    if (window.initMagnetic) window.initMagnetic();
  }, []);

  return (
    <>
      <section className="breadcrumbs">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-wrapper">
                <div className="breadcrumb-cnt">
                  <span>Services Details</span>
                  <h1>Workflow Automation and System Integrations</h1>
                  <div className="breadcrumb-list">
                    <Link to="/">Home</Link>
                    <img src="/assets/img/content/breadcrumb-arrow.svg" alt="" />
                    Services Details
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="service-details">
        <div className="about-services sec-mar">
          <div className="container">
            <div className="row gy-5">
              <div
                className="col-lg-6 d-flex align-items-center wow animate fadeInLeft"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="about-content">
                  <div className="section-title-5">
                    <h2>
                      Workflow Automation <br /> and System Integrations
                    </h2>
                  </div>

                  <p>
                    We design automation workflows that reduce manual handoffs, improve consistency, and keep operations measurable. This includes approvals, routing, notifications, synchronization, and orchestration across the tools your teams already use. The focus is reliability in real-world conditions, not just connecting apps.
                  </p>

                  <p>
                    Automation becomes a liability when it fails silently, duplicates work, or breaks with small upstream changes. We build automations with clear validation, exception handling, audit trails, and monitoring so your workflows remain dependable as volume grows and policies evolve.
                  </p>

                  <ul className="about-featue">
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Workflow mapping, standardization, and automation blueprinting
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      API integrations, webhooks, and event-driven automation
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Data sync and enrichment across CRM, ERP, support, and billing tools
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Exception handling, retries, idempotency, and SLA escalation logic
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Monitoring, alerts, audit logs, and operational dashboards
                    </li>
                    <li>
                      <svg width="15" height="15">
                        <circle cx="7" cy="7" r="7" fill="#c79528" />
                      </svg>
                      Governance: ownership, change control, and documentation
                    </li>
                  </ul>
                </div>
              </div>

              <div
                className="col-lg-6 wow animate fadeInRight"
                data-wow-delay="300ms"
                data-wow-duration="1500ms"
              >
                <div className="service-img magnetic-item">
                  <img className="img-fluid" src="/assets/img/services/laptop.png" alt="Service" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="how-we-work-section sec-mar">
          <div className="container">
            <div
              className="row mb-60 wow animate fadeInUp"
              data-wow-delay="200ms"
              data-wow-duration="1500ms"
            >
              <div className="col-lg-12">
                <div className="section-title">
                  <h2>How We Work</h2>
                </div>
              </div>
            </div>
          </div>

          <div className="work-item">
            <div className="container-fluid">
              <div className="row row-cols-xl-5 row-cols-lg-4 row-cols-md-3 row-cols-sm-2 row-cols-1 gy-5 justify-content-center">
                {workSteps.map((step, idx) => (
                  <div
                    key={step.title}
                    className="col magnetic-item wow animate fadeInUp"
                    data-wow-delay={`${200 + idx * 100}ms`}
                    data-wow-duration="1500ms"
                  >
                    <div className="single-work">
                      <div className="work-icon">
                        <img src={`/assets/img/content/${step.icon}`} alt="" />
                      </div>
                      <div className="work-content">
                        <h3>{step.title}</h3>
                        <p>{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="service-sort-driscription-area sec-mar">
          <div className="container">
            <div className="row">
              <div
                className="col-lg-12 wow animate fadeInUp"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="driscription-img magnetic-item">
                  <img className="img-fluid" src="/assets/img/services/rectangular_image.png" alt="Description" />
                </div>
              </div>

              <div className="driscription-content">
                <div className="row">
                  <div
                    className="col-md-6 wow animate fadeInUp"
                    data-wow-delay="200ms"
                    data-wow-duration="1500ms"
                  >
                    <p>
                      Reliable automation depends on engineering fundamentals. We design integrations with clear data contracts, validation rules, and safe write behavior. That usually includes idempotency keys for create operations, deterministic checks before updates, and retry strategies that do not create duplicate records. We also design workflows with measurable states so teams can see progress, failures, and bottlenecks.
                    </p>

                    <ul className="about-featue">
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Systems of record decisions and data ownership boundaries
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Idempotency, retries, and rate limiting for integration safety
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Exception queues and human review for high-impact cases
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Audit logs and traceability for approvals and data changes
                      </li>
                    </ul>

                    <p>
                      We implement automation using the right layer for the job. For lightweight routing and notifications, low-code tools can be fast and cost-effective. For complex workflows, we use orchestration patterns, background jobs, message queues, and API-first services that are easier to test, secure, and scale.
                    </p>
                  </div>

                  <div
                    className="col-md-6 wow animate fadeInUp"
                    data-wow-delay="300ms"
                    data-wow-duration="1500ms"
                  >
                    <p>
                      Automation needs vary by industry, but the core objectives are consistent: reduce cycle time, reduce errors, and increase predictability. In regulated environments, we prioritize traceability, approvals, and controlled access. In high-volume operations, we prioritize throughput, resilience, and clear exception handling.
                    </p>

                    <ul className="about-featue">
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Finance and procurement: invoice extraction, approvals, policy checks
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Sales and marketing ops: lead routing, enrichment, CRM hygiene
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Support operations: triage, SLA escalation, knowledge-driven workflows
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        HR and IT: onboarding, access provisioning, deprovisioning automation
                      </li>
                      <li>
                        <svg width="15" height="15">
                          <circle cx="7" cy="7" r="7" fill="#c79528" />
                        </svg>
                        Logistics and retail: order updates, inventory sync, exception alerts
                      </li>
                    </ul>

                    <p>
                      Typical deliverables include workflow diagrams, integration specs, monitored automation flows, exception runbooks, and dashboards showing cycle time, exception rate, and throughput. This makes the automation program measurable and maintainable, not dependent on individual knowledge.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="service-faq-area sec-mar">
          <div className="container">
            <div className="row gy-5">
              <div
                className="col-lg-6 wow animate fadeInLeft"
                data-wow-delay="200ms"
                data-wow-duration="1500ms"
              >
                <div className="faq-img magnetic-item">
                  <img className="img-fluid" src="/assets/img/services/faqs.png" alt="FAQ" />
                </div>
              </div>

              <div
                className="col-lg-6 d-flex align-items-center wow animate fadeInRight"
                data-wow-delay="300ms"
                data-wow-duration="1500ms"
              >
                <div className="faq-content-area">
                  <h2>Frequently Asked Questions</h2>

                  <div className="accordion" id="accordionExample">
                    {faqItems.map((item, index) => (
                      <div key={item.question} className="accordion-item">
                        <h6 className="accordion-header" id={`heading${index}`}>
                          <button
                            className={`accordion-button ${index !== 0 ? 'collapsed' : ''}`}
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#collapse${index}`}
                            aria-expanded={index === 0 ? 'true' : 'false'}
                            aria-controls={`collapse${index}`}
                          >
                            {item.question}
                          </button>
                        </h6>

                        <div
                          id={`collapse${index}`}
                          className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                          aria-labelledby={`heading${index}`}
                          data-bs-parent="#accordionExample"
                        >
                          <div className="accordion-body">{item.answer}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServiceWorkflowAutomation;



